import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { PLATFORM_ENVIRONMENT, VerificationResult } from '@platform-frontend/platform';
import {
  ClearNotifications,
  InvalidStateError,
  PlannedError,
  ShowErrorNotification,
  TranslationId,
  convertToHttpParams,
  handleErrors,
  tapNextOrError
} from '@platform-frontend/util';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, take, tap, timeout } from 'rxjs/operators';
import { ACCOUNT_ERROR_HANDLERS } from '../account-error-handlers';
import { SetAuthenticationContext } from '../auth-context';
import { LinkAccountState } from '../link-account';
import { BUILD_LINK_ACCOUNT_ERROR_MSG, LINK_ACCOUNT_ERROR_HANDLERS } from '../link-account/link-account-error-handlers';
import {
  MSG_START_OAUTH_DEFAULT,
  MSG_VERIFY_OAUTH_DEFAULT,
  MSG_VERIFY_OAUTH_DUPLICATE_EMAIL_ADDRESS_DIFFERENT_ACCOUNTS,
  MSG_VERIFY_OAUTH_INVALID_STATE,
  MSG_VERIFY_OAUTH_LINK_ACCOUNT_CONFLICT,
  MSG_VERIFY_OAUTH_LINK_ACCOUNT_MISMATCH,
  MSG_VERIFY_OAUTH_NOT_FOUND,
  MSG_VERIFY_OAUTH_NO_EMAIL_AVAILABLE,
  MSG_VERIFY_OAUTH_SESSION_CORRUPTED
} from '../notification-messages';
import { GotoSignIn, SetSession } from '../session';
import { CompleteOAuthModel } from './model/complete-o-auth-model';
import { OAuthProvider } from './model/o-auth-provider';
import { OAuthStateModel } from './model/o-auth-state-model';
import { StartOAuthModel } from './model/start-o-auth-model';
import { OAuthProviderService } from './o-auth-provider.service';
import { CancelOAuth, CompleteOAuth, StartOAuth, VerifyOAuth } from './o-auth.actions';

const DEFAULT_STATE: OAuthStateModel = {
  provider: null,
  redirectUri: '',
  verified: false,
  verificationRef: ''
};

@State<OAuthStateModel>({
  name: 'oAuth',
  defaults: DEFAULT_STATE
})
@Injectable()
export class OAuthState {
  constructor(private http: HttpClient, private oAuthProviderService: OAuthProviderService, private store: Store) {}

  private static readonly POLLING_TAG = Symbol('OAuthState: PollingTag');

  linkedAccountId$: Observable<string | null> = this.store.select(LinkAccountState.accountId);

  @Selector()
  static provider(state: OAuthStateModel): OAuthProvider | null {
    return state.provider;
  }

  @Selector()
  static redirectUri(state: OAuthStateModel): string {
    return state.redirectUri;
  }

  @Selector()
  static verificationRef(state: OAuthStateModel): string {
    return state.verificationRef;
  }

  @Selector()
  static verified(state: OAuthStateModel): boolean {
    return state.verified;
  }

  @Action(StartOAuth)
  startOAuth(ctx: StateContext<OAuthStateModel>, { provider }: StartOAuth): Observable<unknown> {
    return this.linkedAccountId$.pipe(
      take(1),
      switchMap(linkedAccountId =>
        this.http.get<StartOAuthModel>(PLATFORM_ENVIRONMENT.apiUrl + '/auth/oauth/' + provider, {
          params: convertToHttpParams({ linkedAccountId })
        })
      ),
      tap(startOAuth =>
        ctx.patchState({
          provider,
          redirectUri: startOAuth.redirectUri,
          verificationRef: startOAuth.verificationRef,
          verified: false
        })
      ),
      handleErrors(ACCOUNT_ERROR_HANDLERS, {
        DEFAULT: this.getOAuthDefaultError(provider),
        ...LINK_ACCOUNT_ERROR_HANDLERS(this.store)
      })
    );
  }

  private getOAuthDefaultError(provider: OAuthProvider): ShowErrorNotification {
    const formattedProvider = this.oAuthProviderService.formatProvider(provider);
    const errorMessage = MSG_START_OAUTH_DEFAULT(formattedProvider);
    return new ShowErrorNotification(errorMessage);
  }

  @Action(VerifyOAuth)
  verifyOAuth(ctx: StateContext<OAuthStateModel>, { timeoutInMilliseconds }: VerifyOAuth): Observable<unknown> {
    const params = this.getVerificationRef(ctx);
    const nOptions = { keepExisting: true, tags: [OAuthState.POLLING_TAG] };
    const { provider } = ctx.getState();
    const formattedProvider = this.oAuthProviderService.formatProvider(provider);
    return this.http.get<VerificationResult>(PLATFORM_ENVIRONMENT.apiUrl + '/auth/oauth/verified', { params }).pipe(
      timeout({ each: timeoutInMilliseconds - 50, with: () => throwError(() => new PlannedError('SERVER_UNREACHABLE')) }),
      tap(({ verified }) => ctx.patchState({ verified })),
      tapNextOrError(() => ctx.dispatch(new ClearNotifications(OAuthState.POLLING_TAG))),
      handleErrors({
        DEFAULT: new ShowErrorNotification(MSG_VERIFY_OAUTH_DEFAULT, nOptions),
        NOT_FOUND: () => this.cancel(ctx, MSG_VERIFY_OAUTH_NOT_FOUND),
        NO_EMAIL_AVAILABLE: () => this.cancel(ctx, MSG_VERIFY_OAUTH_NO_EMAIL_AVAILABLE(formattedProvider)),
        DUPLICATE_EMAIL_ADDRESS_DIFFERENT_ACCOUNTS: new ShowErrorNotification(MSG_VERIFY_OAUTH_DUPLICATE_EMAIL_ADDRESS_DIFFERENT_ACCOUNTS('...')),
        INVALID_STATE: () => this.cancel(ctx, MSG_VERIFY_OAUTH_INVALID_STATE(formattedProvider)),
        SESSION_CORRUPTED: () => this.cancel(ctx, MSG_VERIFY_OAUTH_SESSION_CORRUPTED(formattedProvider)),
        USER_CANCELLED: () => this.cancel(ctx),
        LINK_ACCOUNT_CONFLICT: () =>
          this.cancel(
            ctx,
            BUILD_LINK_ACCOUNT_ERROR_MSG(this.store, (vendor: string) => MSG_VERIFY_OAUTH_LINK_ACCOUNT_CONFLICT(vendor))
          ),
        LINK_ACCOUNT_MISMATCH: () =>
          this.cancel(
            ctx,
            BUILD_LINK_ACCOUNT_ERROR_MSG(this.store, (vendor: string) => MSG_VERIFY_OAUTH_LINK_ACCOUNT_MISMATCH(vendor))
          )
      })
    );
  }

  private cancel(ctx: StateContext<OAuthStateModel>, message?: TranslationId): Observable<unknown> {
    if (ctx.getState().verificationRef === '') {
      return EMPTY;
    }

    const nOptions = { keepExisting: true, tags: [OAuthState.POLLING_TAG] };
    return ctx.dispatch(new CancelOAuth()).pipe(switchMap(() => (message ? ctx.dispatch(new ShowErrorNotification(message, nOptions)) : EMPTY)));
  }

  @Action(CompleteOAuth)
  completeOAuth(ctx: StateContext<OAuthStateModel>): Observable<unknown> {
    return this.http
      .post<CompleteOAuthModel>(PLATFORM_ENVIRONMENT.apiUrl + '/auth/oauth/login', this.getVerificationRef(ctx))
      .pipe(switchMap(result => ctx.dispatch([new SetAuthenticationContext(result.firstSignUp ? 'SIGN_UP' : 'SIGN_IN'), new SetSession(result.session)])));
  }

  private getVerificationRef(ctx: StateContext<OAuthStateModel>): { verificationRef: string } {
    const { verificationRef } = ctx.getState();

    if (verificationRef === '') {
      throw new InvalidStateError('OAuth is not initialized.');
    }

    return { verificationRef };
  }

  @Action(CancelOAuth)
  cancelOAuth(ctx: StateContext<OAuthStateModel>): Observable<unknown> {
    const { verificationRef } = ctx.getState();
    if (verificationRef === '') {
      return EMPTY;
    }

    ctx.patchState(DEFAULT_STATE);
    return this.http.post<void>(PLATFORM_ENVIRONMENT.apiUrl + '/auth/oauth/cancel', { verificationRef }).pipe(
      catchError(() => of(null)),
      switchMap(() => ctx.dispatch(new GotoSignIn()))
    );
  }
}
